"use client";

import {useEffect, useState, useTransition} from "react";

export function JoinLobbyForm() {
    const [code, setCode] = useState("");
    const [playerKind, setPlayerKind] = useState<"session" | "guest" | null>(null);
    const [guestName, setGuestName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isPending, startTransition] = useTransition();

    useEffect(() => {
        let cancelled = false;

        async function loadPlayer() {
            try {
                const response = await fetch("/api/auth/player", {cache: "no-store"});
                const result = await response.json() as {error?: string; player?: {name: string; kind: "session" | "guest"}};
                if (!response.ok || !result.player) {
                    throw new Error(result.error ?? "Failed to initialize player");
                }
                if (!cancelled) {
                    setPlayerKind(result.player.kind);
                    if (result.player.kind === "guest") {
                        setGuestName(result.player.name);
                    }
                }
            } catch (cause) {
                console.error("Failed to prepare guest identity for lobby join.", cause);
                if (!cancelled) {
                    setError(cause instanceof Error ? cause.message : "Failed to initialize player");
                }
            }
        }

        void loadPlayer();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="join-bar">
            {playerKind === "guest" ? (
                <input
                    className="join-input"
                    maxLength={24}
                    onChange={(event) => setGuestName(event.target.value)}
                    placeholder="NICKNAME"
                    value={guestName}
                />
            ) : null}
            <input
                className="join-input"
                maxLength={6}
                onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                placeholder="ENTER CODE"
                value={code}
            />
            <button
                className="button button-primary"
                disabled={isPending || code.length < 5 || (playerKind === "guest" && guestName.trim().length === 0)}
                type="button"
                onClick={() => {
                    startTransition(async () => {
                        try {
                            setError(null);
                            if (playerKind === "guest") {
                                const renameResponse = await fetch("/api/auth/player", {
                                    method: "POST",
                                    headers: {"content-type": "application/json"},
                                    body: JSON.stringify({name: guestName})
                                });
                                const renameResult = await renameResponse.json() as {error?: string};
                                if (!renameResponse.ok) {
                                    throw new Error(renameResult.error ?? "Failed to save guest nickname");
                                }
                            }

                            const response = await fetch(`/api/lobbies/code/${encodeURIComponent(code)}`);
                            const result = await response.json() as {lobbyId?: string; error?: string};
                            if (!response.ok || !result.lobbyId) {
                                setError(result.error ?? "Lobby not found");
                                return;
                            }
                            window.location.href = `/lobbies/${result.lobbyId}`;
                        } catch (cause) {
                            console.error("Failed to prepare lobby join.", cause);
                            setError(cause instanceof Error ? cause.message : "Failed to join lobby");
                        }
                    });
                }}
            >
                {isPending ? "..." : "Join"}
            </button>
            {error ? <span className="inline-note">{error}</span> : null}
        </div>
    );
}
